/**
 * AdminFatturePage — F-011 (rescoped 2026-07-08).
 *
 * Schermata ADMIN per la coda "fatture da emettere": una riga per pagamento
 * Stripe riuscito (ricarica wallet o pay-per-practice). Niente SDI in V1 —
 * l'admin fattura manualmente dal gestionale esterno e marca la voce qui.
 *
 * Struttura (pattern admin-servizi): guard ADMIN + AppLayout + QueryClientProvider
 * esterno; `AdminFatturePageInner` interno (esportato per i test).
 */
import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { QueryClientProvider } from '@tanstack/react-query';
import type { FatturaStato } from '@app/shared';
import { RequireAuth } from '@/features/auth/components/RequireAuth';
import { AppLayout } from '@/components/layout/AppLayout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select } from '@/components/ui/select';

import { fattureQueryClient } from '../lib/query-client';
import { errorMessageFor } from '../lib/fatture-client';
import { useFatture, useMarkFatturata } from '../hooks/useFatture';
import { FatturaRow } from '../components/FatturaRow';

export function AdminFatturePage() {
  return (
    <RequireAuth roles={['ADMIN']}>
      <AppLayout>
        <QueryClientProvider client={fattureQueryClient}>
          <AdminFatturePageInner />
        </QueryClientProvider>
      </AppLayout>
    </RequireAuth>
  );
}

export function AdminFatturePageInner() {
  const [stato, setStato] = useState<FatturaStato | ''>('DA_FATTURARE');
  const query = useFatture({ stato: stato || undefined, limit: 100 });
  const markFatturata = useMarkFatturata();

  const items = query.data?.items ?? [];
  const pendingCount = stato === 'DA_FATTURARE' ? query.data?.total ?? 0 : undefined;

  return (
    <section className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-3xl font-semibold tracking-tight">Fatture da emettere</h1>
        <p className="text-muted-foreground">
          Promemoria dei pagamenti confermati (ricarica wallet, pagamento pratica) in attesa di
          fattura manuale dal gestionale esterno.
          {pendingCount !== undefined ? (
            <span className="ml-1 font-medium text-foreground">
              {pendingCount} in attesa.
            </span>
          ) : null}
        </p>
      </header>

      <div className="flex items-center gap-2">
        <label htmlFor="fatture-filtro-stato" className="text-sm text-muted-foreground">
          Filtra per stato
        </label>
        <Select
          id="fatture-filtro-stato"
          value={stato}
          onChange={(e) => setStato(e.target.value as FatturaStato | '')}
          className="w-48"
        >
          <option value="">Tutte</option>
          <option value="DA_FATTURARE">Da fatturare</option>
          <option value="FATTURATA">Fatturate</option>
        </Select>
      </div>

      {query.isError ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Errore</AlertTitle>
          <AlertDescription>{errorMessageFor(query.error)}</AlertDescription>
        </Alert>
      ) : null}

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground" aria-live="polite">
          Caricamento fatture…
        </p>
      ) : null}

      {!query.isLoading && !query.isError ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Voci ({items.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {items.length === 0 ? (
              <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
                Nessuna voce per il filtro selezionato.
              </div>
            ) : (
              <table className="w-full border-collapse text-sm" aria-label="Fatture da emettere">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th scope="col" className="py-2 pr-4 font-medium">Data</th>
                    <th scope="col" className="py-2 pr-4 font-medium">Cliente</th>
                    <th scope="col" className="py-2 pr-4 font-medium">Tipo</th>
                    <th scope="col" className="py-2 pr-4 font-medium">Importo</th>
                    <th scope="col" className="py-2 pr-4 font-medium">Riferimento</th>
                    <th scope="col" className="py-2 pr-4 font-medium">Stato</th>
                    <th scope="col" className="py-2 text-right font-medium">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((fattura) => (
                    <FatturaRow
                      key={fattura.id}
                      fattura={fattura}
                      onMarkFatturata={(body) => markFatturata.mutate({ id: fattura.id, body })}
                      isSaving={markFatturata.isPending && markFatturata.variables?.id === fattura.id}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      ) : null}
    </section>
  );
}
